/**
 * ✅ Optimized Production Dashboard
 * - Prevents duplicate API calls (true StrictMode safety)
 * - Minimized re-renders with proper memoization
 * - Efficient rendering with stable references
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { apiService } from '../services/api';
import { API_CONFIG, API_ENDPOINTS } from '../config/apiConfig';
import bigAtomLogo from '../assets/images/big-atom-logo copy.png';
import { ReactComponent as DashboardIcon } from '../assets/icons/dashboard.svg';
import { ReactComponent as PixelsIcon } from '../assets/icons/pixels.svg';
import { ReactComponent as SetupCapiIcon } from '../assets/icons/setup_capi.svg';
import './Dashboard.css';
import DrillDownModal from '../components/common/DrillDownModal';
import SettingsInline from '../components/dashboard/SettingsInline';

// Cache key for dashboard data
const DASHBOARD_CACHE_BASE_KEY = 'dashboard_cache';
const CACHE_EXPIRY_TIME = 5 * 60 * 1000; // 5 minutes

const DEFAULT_USER_PROFILE = {
  email: 'Meta Admin',
  role: 'Administrator',
  orgId: null,
  orgName: '',
};

const cloneDefaultProfile = () => ({ ...DEFAULT_USER_PROFILE });

const normalizeRoleLabel = (role) => {
  if (!role) {
    return DEFAULT_USER_PROFILE.role;
  }

  if (Array.isArray(role)) {
    return normalizeRoleLabel(role[0]);
  }

  if (typeof role === 'object') {
    return normalizeRoleLabel(role.role || role.name || role.value || role.authority);
  }

  const roleString = role.toString().trim();
  if (!roleString) {
    return DEFAULT_USER_PROFILE.role;
  }

  const cleanedRole = roleString.replace(/^ROLE[_\s-]*/i, '');
  const formatted = cleanedRole
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');

  return formatted || DEFAULT_USER_PROFILE.role;
};

const isRoleAdmin = (role) => {
  if (!role) {
    return false;
  }
  if (Array.isArray(role)) {
    return role.some((item) => isRoleAdmin(item));
  }
  if (typeof role === 'object' && role !== null) {
    return isRoleAdmin(role.role || role.name || role.value || role.authority);
  }
  const roleString = role.toString().trim().toUpperCase();
  if (!roleString) {
    return false;
  }
  return roleString === 'ADMIN' || roleString === 'ROLE_ADMIN' || roleString.includes('ADMIN');
};

const getUserProfileFromStorage = () => {
  if (typeof window === 'undefined') {
    return cloneDefaultProfile();
  }

  try {
    const storedProfile = window.localStorage.getItem('user_profile');
    if (storedProfile) {
      const parsedProfile = JSON.parse(storedProfile);
      return {
        email: parsedProfile?.email || DEFAULT_USER_PROFILE.email,
        role: parsedProfile?.role || DEFAULT_USER_PROFILE.role,
        orgId: parsedProfile?.orgId ?? DEFAULT_USER_PROFILE.orgId,
        orgName: parsedProfile?.orgName || '',
      };
    }
  } catch (storageError) {
    console.warn('Unable to read user profile from storage:', storageError);
  }

  return cloneDefaultProfile();
};

const statusClass = (status) => {
  const normalized = (status || '').toString().toLowerCase();
  if (normalized.includes('active')) return 'active';
  if (normalized.includes('pending') || normalized.includes('attention') || normalized.includes('review')) return 'pending';
  if (normalized.includes('inactive') || normalized.includes('critical') || normalized.includes('error') || normalized.includes('disabled')) return 'inactive';
  return 'active';
};

const formatStatusLabel = (status) => {
  const normalized = (status || '').toString().trim();
  if (!normalized) return 'Active';
  return normalized.charAt(0).toUpperCase() + normalized.slice(1).toLowerCase();
};

const safeNumber = (value) => {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string') {
    const cleaned = value.replace(/[^\d.-]/g, '');
    if (!cleaned) {
      return null;
    }
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const formatAverageScore = (value) => {
  const numeric = safeNumber(value);
  if (numeric === null) {
    return '—';
  }
  return numeric.toFixed(1);
};

const normalizePixelRow = (pixel) => {
  const emqValue = safeNumber(pixel.emq ?? pixel.emqScore ?? pixel.EMQ);
  const odqValue = safeNumber(pixel.odq ?? pixel.odqScore ?? pixel.ODQ);

  return {
    id: pixel.id || pixel.pixelId || pixel.dataset_id,
    dataset_id: pixel.dataset_id || pixel.id || pixel.pixelId || pixel.id,
    name: pixel.name || pixel.pixelName || 'Unnamed Pixel',
    pixelId: pixel.id || pixel.pixelId || pixel.dataset_id || '—',
    emq: emqValue,
    odq: odqValue,
    capiStatus: pixel.capiStatus || pixel.capi_status || pixel.status || 'Unknown',
    lastUpdated:
      pixel.lastUpdated ||
      pixel.last_updated ||
      pixel.lastUpdatedAt ||
      pixel.updatedAt ||
      null,
  };
};

const getEmqScoreColor = (value) => {
  const numeric = safeNumber(value);
  if (numeric === null) {
    return '#6c757d';
  }
  if (numeric >= 8) {
    return '#15803d';
  }
  if (numeric >= 5) {
    return '#b45309';
  }
  return '#b91c1c';
};

// Helper function to determine color class based on EMQ/ODQ value
const getScoreClass = (value) => {
  const numeric = safeNumber(value);
  if (numeric === null) return 'score-na';
  if (numeric >= 8) return 'score-good';
  if (numeric >= 5) return 'score-warning';
  return 'score-poor';
};

// Helper function to get cached data
const getCacheKey = (orgId) =>
  `${DASHBOARD_CACHE_BASE_KEY}_${orgId === undefined || orgId === null ? 'unknown' : orgId}`;

const getCachedData = (orgId) => {
  if (orgId === undefined || orgId === null) {
    return null;
  }
  try {
    const cacheKey = getCacheKey(orgId);
    const cached = sessionStorage.getItem(cacheKey);
    if (!cached) return null;
    
    const { metrics, pixels, timestamp, lastUpdatedAt } = JSON.parse(cached);
    const now = Date.now();
    
    if (now - timestamp < CACHE_EXPIRY_TIME) {
      return { metrics, pixels, timestamp, lastUpdatedAt: lastUpdatedAt ?? null };
    }
    
    sessionStorage.removeItem(cacheKey);
    return null;
  } catch (err) {
    console.error('Error reading cache:', err);
    return null;
  }
};

// Helper function to save data to cache
const saveToCache = (
  orgId,
  metrics,
  pixels,
  { cacheTimestamp = Date.now(), lastUpdatedTimestamp = null } = {}
) => {
  if (orgId === undefined || orgId === null) {
    return;
  }
  try {
    const cacheData = {
      metrics,
      pixels,
      timestamp: cacheTimestamp,
      lastUpdatedAt: lastUpdatedTimestamp,
    };

    sessionStorage.setItem(getCacheKey(orgId), JSON.stringify(cacheData));
  } catch (err) {
    console.error('Error saving cache:', err);
  }
};

// Map URL paths to tab names
const pathToTabMap = {
  '/dashboard': 'overview',
  '/pixels': 'pixels',
  '/setup-capi': 'setupCapi',
};

// Map tab names to URL paths
const tabToPathMap = {
  'overview': '/dashboard',
  'pixels': '/pixels',
  'setupCapi': '/setup-capi',
};

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Sync activeTab with current URL
  useEffect(() => {
    const currentPath = location.pathname;
    const tabFromPath = pathToTabMap[currentPath];
    if (tabFromPath) {
      setActiveTab(tabFromPath);
    }
  }, [location.pathname]);
  
  const [activeTab, setActiveTab] = useState(() => {
    // Initialize from URL if available
    const path = location.pathname;
    return pathToTabMap[path] || 'overview';
  });
  const [metrics, setMetrics] = useState({
    totalActivePixels: null,
    healthyPixels: null,
    needAttentionPixels: null,
  });
  const [pixelData, setPixelData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [metaWarning, setMetaWarning] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPixel, setSelectedPixel] = useState(null);
  const [userProfile, setUserProfile] = useState(() => getUserProfileFromStorage());
  const [metaStatus, setMetaStatus] = useState({ connected: false, tokenExpiresAt: null });
  const [metaStatusLoading, setMetaStatusLoading] = useState(false);
  const [settingsSection, setSettingsSection] = useState('alert-preferences');
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [adminOrgStatuses, setAdminOrgStatuses] = useState([]);
  const [adminOrgLoading, setAdminOrgLoading] = useState(false);
  const [adminOrgError, setAdminOrgError] = useState(null);
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState(null);

  const isAdmin = useMemo(() => isRoleAdmin(userProfile?.role), [userProfile?.role]);

  const activeOrgId = useMemo(() => {
    if (isAdmin) {
      const parsed =
        typeof selectedOrgId === 'string'
          ? Number(selectedOrgId)
          : typeof selectedOrgId === 'number'
          ? selectedOrgId
          : null;
      return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    }
    const orgId = userProfile?.orgId;
    const parsed =
      typeof orgId === 'string'
        ? Number(orgId)
        : typeof orgId === 'number'
        ? orgId
        : null;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [isAdmin, selectedOrgId, userProfile]);

  const adminOrgOptions = useMemo(
    () =>
      adminOrgStatuses.map((org) => ({
        orgId: Number(org.orgId),
        orgName: org.orgName || `Org ${org.orgId}`,
        status: org.status || 'UNKNOWN',
      })),
    [adminOrgStatuses]
  );

  const selectedOrgStatus = useMemo(
    () => adminOrgOptions.find((org) => org.orgId === activeOrgId) || null,
    [adminOrgOptions, activeOrgId]
  );

  const fetchMetaStatus = useCallback(
    async (silent = false) => {
      const orgId = activeOrgId;
      const defaultStatus = {
        connected: false,
        tokenExpiresAt: null,
        metaUserName: null,
        metaUserPictureUrl: null,
      };
      if (!orgId) {
        setMetaStatus(defaultStatus);
        return defaultStatus;
      }
      if (!silent) {
        setMetaStatusLoading(true);
      }
      try {
        const response = await apiService.get(API_ENDPOINTS.META_OAUTH_STATUS(orgId));
        const status = {
          connected: Boolean(response?.connected),
          tokenExpiresAt: response?.tokenExpiresAt || null,
          metaUserName: response?.metaUserName || null,
          metaUserPictureUrl: response?.metaUserPictureUrl || null,
        };
        setMetaStatus(status);
        return status;
      } catch (err) {
        console.error('Failed to fetch Meta connection status:', err);
        setMetaStatus(defaultStatus);
        return defaultStatus;
      } finally {
        if (!silent) {
          setMetaStatusLoading(false);
        }
      }
    },
    [activeOrgId]
  );

  // Use a ref to track if data has been fetched successfully
  const hasFetchedRef = useRef(false);
  const abortControllerRef = useRef(null);
  const userMenuRef = useRef(null);

  useEffect(() => {
    if (!isAdmin) {
      setAdminOrgStatuses([]);
      setSelectedOrgId(null);
      setAdminOrgError(null);
      return;
    }

    let isMounted = true;

    const loadOrgStatuses = async () => {
      try {
        setAdminOrgLoading(true);
        setAdminOrgError(null);
        const response = await apiService.get(API_ENDPOINTS.META_OAUTH_ORG_STATUSES);
        if (!isMounted) {
      return;
    }
        const normalized = Array.isArray(response)
          ? response.map((org) => ({
              orgId: Number(org.orgId),
              orgName: org.orgName || `Org ${org.orgId}`,
              status: org.status || 'UNKNOWN',
            }))
          : [];
        setAdminOrgStatuses(normalized);
        setSelectedOrgId((prev) => {
          if (prev && normalized.some((org) => org.orgId === prev)) {
            return prev;
          }
          return normalized.length ? normalized[0].orgId : null;
        });
      } catch (err) {
        console.error('Failed to load admin organization statuses:', err);
        if (isMounted) {
          setAdminOrgError(
            err.response?.data?.message ||
              err.message ||
              'Unable to load organizations. Please try again later.'
          );
          setAdminOrgStatuses([]);
          setSelectedOrgId(null);
        }
      } finally {
        if (isMounted) {
          setAdminOrgLoading(false);
        }
      }
    };

    loadOrgStatuses();

    return () => {
      isMounted = false;
    };
  }, [isAdmin]);

  // Extract fetch logic into reusable function
  const fetchDashboardData = useCallback(
    async (skipCache = false, signal = null, skipLoadingState = false) => {
      const orgId = activeOrgId;
      try {
        if (!skipLoadingState) {
        setLoading(true);
        }
        setError(null);

        if (!orgId) {
          console.warn('No organization ID available while attempting to fetch dashboard data.');
          setPixelData([]);
          setMetrics({
            totalActivePixels: null,
            healthyPixels: null,
            needAttentionPixels: null,
          });
          setError('No organization is associated with this account. Please sign in again or contact support.');
          return;
        }

        console.log(`🔄 Starting dashboard data fetch for org ${orgId}...`);

        const [metricsResponse, pixelsResponse, lastUpdatedResponse] = await Promise.all([
          apiService.get(API_ENDPOINTS.METRICS(orgId), { signal }),
          apiService.get(API_ENDPOINTS.PIXELS(orgId), { signal }),
          apiService
            .get(API_ENDPOINTS.DATASETS_LAST_UPDATED(orgId), { signal })
            .catch((err) => {
              if (err.response?.status === 204) {
                return null;
              }
              throw err;
            }),
        ]);

        const metricsData = {
          totalActivePixels: metricsResponse.totalActivePixels ?? metricsResponse.total_active_pixels ?? 0,
          healthyPixels: metricsResponse.healthyPixels ?? metricsResponse.healthy_pixels ?? 0,
          needAttentionPixels: metricsResponse.needAttentionPixels ?? metricsResponse.need_attention_pixels ?? 0,
        };

        const mappedPixelData = Array.isArray(pixelsResponse)
          ? pixelsResponse.map((pixel) => normalizePixelRow(pixel))
          : [];

        let resolvedLastUpdated = null;
        if (lastUpdatedResponse && typeof lastUpdatedResponse === 'object') {
          const raw =
            lastUpdatedResponse.lastUpdatedAt ||
            lastUpdatedResponse.last_updated_at ||
            lastUpdatedResponse.last_seen_at;
          if (raw) {
            const parsed = new Date(raw);
            if (!Number.isNaN(parsed.getTime())) {
              resolvedLastUpdated = parsed;
            }
          }
        }

          setMetrics(metricsData);
          setPixelData(mappedPixelData);
        setError(null);
        hasFetchedRef.current = true;
        setLastRefreshedAt(resolvedLastUpdated);
        setInitialLoadComplete(true);

        saveToCache(orgId, metricsData, mappedPixelData, {
          cacheTimestamp: Date.now(),
          lastUpdatedTimestamp: resolvedLastUpdated ? resolvedLastUpdated.getTime() : null,
        });
          
          console.log('✅ Dashboard data loaded successfully');
          console.log(`📊 Metrics: ${JSON.stringify(metricsData)}`);
          console.log(`📊 Pixels: ${mappedPixelData.length} items`);
      } catch (err) {
        if (err.name === 'AbortError' || err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
          console.log('🚫 Fetch aborted/canceled (expected in StrictMode or unmount)');
          return;
        }

        console.error('❌ Error fetching dashboard data:', err);

          if (err.response) {
            const status = err.response.status;
            if (status === 404) {
              setError(
              `Organization not found (404). Please verify:\n` +
                `1. Org ID: ${orgId}\n` +
                  `2. Endpoint path matches backend\n` +
                  `3. Backend is running and accessible`
              );
            } else if (status === 500) {
              setError('Server error. Please try again later.');
            } else {
              setError(`Failed to load dashboard data: ${err.response.data?.message || 'Unknown error'}`);
            }
          } else if (err.request) {
            setError(
              `Unable to connect to server at ${API_CONFIG.BASE_URL}. ` +
              `Check backend status, port, and CORS configuration.`
            );
          } else {
            setError(`Unexpected error: ${err.message}`);
          }
        throw err;
      } finally {
        if (!skipLoadingState) {
          setLoading(false);
        }
      }
    },
    [activeOrgId]
  );

  // Handle sync/resync action (trigger backend refresh then re-fetch)
  const handleSync = useCallback(async () => {
    const orgId = activeOrgId;
    try {
      setSyncing(true);
      setError(null);

      const metaConnectionStatus = await fetchMetaStatus(true);
      if (!metaConnectionStatus?.connected) {
        setMetaWarning('Hey! You are not connected to META. Kindly connect to META for fetching pixels.');
        return;
      }

      if (!orgId) {
        setError('Cannot refresh data because no organization is associated with this account.');
        return;
      }
      console.log(`🔄 Refreshing datasets for org ${orgId}...`);

      await apiService.post(API_ENDPOINTS.REFRESH_DATASETS(orgId), null, { timeout: 0 });

      sessionStorage.removeItem(getCacheKey(orgId));

      console.log('✅ Refresh request completed, updating dashboard data...');
      await fetchDashboardData(true, null, true);
      console.log('✅ Dashboard data refreshed');
    } catch (err) {
      console.error('❌ Error refreshing dashboard:', err);
      if (err.response) {
        setError(`Refresh failed: ${err.response.data?.message || err.response.data || 'Server error. Please try again later.'}`);
      } else if (err.request) {
        setError('Unable to connect to server. Please check your connection and try again.');
      } else {
        setError(`Refresh failed: ${err.message}`);
        }
      } finally {
      setSyncing(false);
    }
  }, [activeOrgId, fetchDashboardData, fetchMetaStatus]);

  const handleRefreshPixels = useCallback(() => {
    handleSync();
  }, [handleSync]);

  const handleExportPixels = useCallback(() => {
    const headers = ['Pixel Name', 'Pixel ID', 'Web EMQ', 'Offline EMQ', 'Capi Status'];
    const query = searchQuery.trim().toLowerCase();

    const dataset = Array.isArray(pixelData) ? pixelData : [];

    const filteredPixels = query
      ? dataset.filter((pixel) => {
          const name = (pixel.name || pixel.dataset_id || '').toString().toLowerCase();
          const id = (pixel.pixelId || pixel.dataset_id || pixel.id || '').toString().toLowerCase();
          return name.includes(query) || id.includes(query);
        })
      : dataset;

    if (!filteredPixels.length) {
      console.warn('No pixel data available to export.');
      return;
    }

    const escapeCSV = (value) => {
      if (value === null || value === undefined) {
        return '';
      }

      const stringValue = value.toString();
      if (/[",\n]/.test(stringValue)) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };

    const rows = filteredPixels.map((pixel) => [
      pixel.name || pixel.dataset_id || pixel.id || 'Unnamed Pixel',
      pixel.pixelId || pixel.dataset_id || pixel.id || '',
      formatAverageScore(pixel.emq),
      formatAverageScore(pixel.odq),
      formatStatusLabel(pixel.capiStatus || 'Active'),
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => escapeCSV(cell)).join(','))
      .join('\n');

    const blob = new Blob([`\ufeff${csvContent}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const timestamp = new Date().toISOString().split('T')[0];
    link.href = url;
    link.download = `pixels-export-${timestamp}.csv`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [pixelData, searchQuery]);

  useEffect(() => {
    hasFetchedRef.current = false;
  }, [activeOrgId]);

  useEffect(() => {
    if (hasFetchedRef.current) {
      console.log('✅ Data already fetched, skipping');
      return;
    }

    if (!activeOrgId) {
      if (isAdmin) {
        if (!adminOrgLoading) {
          setPixelData([]);
          setMetrics({
            totalActivePixels: null,
            healthyPixels: null,
            needAttentionPixels: null,
          });
          setLastRefreshedAt(null);
          setInitialLoadComplete(true);
          setLoading(false);
        }
        return;
      }
      console.warn('No organization ID present; skipping dashboard data load.');
      setPixelData([]);
      setMetrics({
        totalActivePixels: null,
        healthyPixels: null,
        needAttentionPixels: null,
      });
      setError('No organization is associated with this account. Please sign in again or contact support.');
      setLoading(false);
      return;
    }

    const cached = getCachedData(activeOrgId);
    if (cached) {
      console.log('📦 Loading data from cache');
      setMetrics(cached.metrics);
      setPixelData(cached.pixels);
      setLoading(false);
      const cachedTimestamp = cached.lastUpdatedAt ?? cached.timestamp;
      if (cachedTimestamp) {
        const parsed = new Date(cachedTimestamp);
        if (!Number.isNaN(parsed.getTime())) {
          setLastRefreshedAt(parsed);
        } else {
          setLastRefreshedAt(null);
        }
      } else {
        setLastRefreshedAt(null);
      }
      setInitialLoadComplete(true);
      hasFetchedRef.current = true;
      return;
    }

    setLoading(true);

    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    let isMounted = true;

    const loadData = async () => {
      try {
        await fetchDashboardData(false, signal);
      } catch (err) {
        if (err.name === 'AbortError' || err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
          console.log('🚫 Load data aborted (expected in StrictMode)');
          return;
        }
        if (!isMounted || signal.aborted) {
          return;
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [activeOrgId, adminOrgLoading, fetchDashboardData, isAdmin]);

  useEffect(() => {
    if (activeOrgId) {
      fetchMetaStatus(true);
    } else {
      setMetaStatus({ connected: false, tokenExpiresAt: null });
    }
  }, [activeOrgId, fetchMetaStatus]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    setUserProfile(getUserProfileFromStorage());

    const handleStorageUpdate = (event) => {
      if (event.key === 'user_profile') {
        setUserProfile(getUserProfileFromStorage());
      }
    };

    window.addEventListener('storage', handleStorageUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageUpdate);
    };
  }, []);

  useEffect(() => {
    if (!userMenuOpen) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [userMenuOpen]);

  useEffect(() => {
    setUserMenuOpen(false);
  }, [activeTab]);

  const handleRetry = useCallback(() => {
    hasFetchedRef.current = false;
    setError(null);
      setMetaWarning(null);
    setError(null);
    setMetaWarning(null);
    setError(null);
    setMetaWarning(null);
    setLoading(true);
    window.location.reload();
  }, []);

  const handleSidebarChange = useCallback(
    (tab, options = {}) => {
      setActiveTab(tab);
      // Navigate to the corresponding URL (except for settings which stays inline)
      if (tab === 'settings') {
        setSettingsSection(options.section || 'alert-preferences');
        // Don't navigate - keep settings inline in Dashboard
      } else {
        const path = tabToPathMap[tab];
        if (path && location.pathname !== path) {
          navigate(path, { replace: true });
        }
      }
    },
    [navigate, location.pathname]
  );

  const handleUserMenuToggle = useCallback(() => {
    setUserMenuOpen((prev) => !prev);
  }, []);

  const handleSettingsOpen = useCallback(() => {
    handleSidebarChange('settings');
    setUserMenuOpen(false);
  }, [handleSidebarChange]);

  const handleOrgChange = useCallback((event) => {
    const { value } = event.target;
    const parsed = Number(value);
    setSelectedOrgId(Number.isFinite(parsed) && parsed > 0 ? parsed : null);
    setSearchQuery('');
    setMetaWarning(null);
    setError(null);
    setIsModalOpen(false);
    setSelectedPixel(null);
    hasFetchedRef.current = false;
  }, []);

  const handleSearchChange = useCallback((value) => {
    setSearchQuery(value);
    if (value.trim().length > 0 && activeTab !== 'pixels') {
      setActiveTab('pixels');
    }
  }, [activeTab]);

  const handleRowClick = useCallback((pixel) => {
    setSelectedPixel(pixel);
    setIsModalOpen(true);
  }, []);

  const handleEnableCapi = useCallback(() => {
    setActiveTab('setupCapi');
  }, []);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setSelectedPixel(null);
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_profile');
    setUserProfile(cloneDefaultProfile());
    navigate('/login');
  }, [navigate, setUserProfile]);

  const displayEmail = userProfile.email || DEFAULT_USER_PROFILE.email;

  const handleGetStartedNow = useCallback(async () => {
    if (emailSending) return;

    setEmailSending(true);
    setEmailError(null);
    setEmailSent(false);

    try {
      const emailHtml = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
            .container { max-width: 600px; margin: 0 auto; padding: 20px; }
            .header { background: #6148F6; color: white; padding: 20px; border-radius: 8px 8px 0 0; }
            .content { background: #f9f9f9; padding: 20px; border-radius: 0 0 8px 8px; }
            .info-box { background: white; padding: 15px; border-left: 4px solid #6148F6; margin: 15px 0; }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <h2 style="margin: 0;">CAPI Enablement Request</h2>
        </div>
            <div class="content">
              <p>Hello,</p>
              <p>A user has requested to enable CAPI (Conversions API) for their account.</p>
              <div class="info-box">
                <p><strong>User Email:</strong> ${displayEmail}</p>
                <p><strong>Request Date:</strong> ${new Date().toLocaleString()}</p>
      </div>
              <p>Please review and process this request at your earliest convenience.</p>
              <p>Best regards,<br>BigAtom Platform</p>
            </div>
          </div>
        </body>
        </html>
      `;

      const response = await apiService.post(API_ENDPOINTS.SEND_EMAIL, {
        from: 'noreply@bigatom.com', // You may want to configure this
        to: ['sahil.sapehia@adyogi.com', 'sriram@adyogi.com'],
        subject: 'CAPI Enablement Request',
        html: emailHtml,
      });

      console.log('Email API Response:', response);

      // apiService.post returns response.data directly, not the full response object
      // The backend returns { "success": true, "message": "Email sent successfully" }
      if (response?.success === true || response?.success === 'true') {
        setEmailSent(true);
        // Clear success message after 5 seconds
        setTimeout(() => {
          setEmailSent(false);
        }, 5000);
      } else {
        setEmailError(response?.message || 'Failed to send email. Please try again.');
      }
    } catch (error) {
      console.error('Failed to send email:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        'Failed to send email. Please try again later.';
      setEmailError(errorMessage);
    } finally {
      setEmailSending(false);
    }
  }, [displayEmail, emailSending]);
  const displayRole = useMemo(() => normalizeRoleLabel(userProfile.role), [userProfile.role]);
  const displayOrgName = useMemo(() => {
    if (isAdmin && selectedOrgStatus) {
      return selectedOrgStatus.orgName;
    }
    const name = userProfile?.orgName;
    return typeof name === 'string' ? name.trim() : '';
  }, [isAdmin, selectedOrgStatus, userProfile?.orgName]);
  const avatarInitial = useMemo(() => {
    const emailSource = (displayEmail || '').trim();
    if (emailSource) {
      return emailSource.charAt(0).toUpperCase();
    }
    return 'B';
  }, [displayEmail]);
  const formattedLastRefreshed = useMemo(() => {
    if (!lastRefreshedAt || !(lastRefreshedAt instanceof Date)) {
      return null;
    }
    try {
      return new Intl.DateTimeFormat('en-US', {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(lastRefreshedAt);
    } catch (err) {
      console.warn('Unable to format last refreshed timestamp:', err);
      return null;
    }
  }, [lastRefreshedAt]);

  const tableColumns = useMemo(
    () => [
      {
        key: 'name',
        label: 'Pixel Name',
        render: (value) => <span className="pixel-name">{value}</span>,
      },
      {
        key: 'pixelId',
        label: 'Pixel ID',
        render: (_, row) => <span className="pixel-id">{row.pixelId || row.dataset_id || row.id}</span>,
      },
      {
        key: 'webEmq',
        label: 'Web EMQ',
        render: (_, row) => (
          <span
            className="pixel-score"
            style={{ color: getEmqScoreColor(row.emq) }}
          >
            {formatAverageScore(row.emq)}
          </span>
        ),
      },
      {
        key: 'offlineEmq',
        label: 'Offline EMQ',
        render: (_, row) => (
          <span
            className="pixel-score"
            style={{ color: getEmqScoreColor(row.odq) }}
          >
            {formatAverageScore(row.odq)}
          </span>
        ),
      },
      {
        key: 'status',
        label: 'Capi Status',
        render: (_, row) => {
          const status = (row.capiStatus || 'unknown').toString().toLowerCase();
          const display = (row.capiStatus || 'Unknown').toString().toUpperCase();
          return <span className={`pixel-status-badge status-${status}`}>{display}</span>;
        },
      },
      {
        key: 'actions',
        label: 'Actions',
        render: (_, row) => (
          <button type="button" className="pixels-action-btn" onClick={() => handleRowClick(row)}>
            View Quality
          </button>
        ),
      },
    ],
    [handleRowClick]
  );

  const displayPixelData = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const normalized = query
      ? pixelData.filter((pixel) => {
      const name = (pixel.name || '').toLowerCase();
      const id = (pixel.pixelId || pixel.dataset_id || pixel.id || '').toString().toLowerCase();
      return name.includes(query) || id.includes(query);
      })
      : pixelData;

    return [...normalized].sort((a, b) => {
      const aDisabled = String(a.capiStatus || '').toLowerCase().includes('disabl');
      const bDisabled = String(b.capiStatus || '').toLowerCase().includes('disabl');
      if (aDisabled === bDisabled) return 0;
      return aDisabled ? 1 : -1;
    });
  }, [pixelData, searchQuery]);

  // Filtered pixel data for dashboard tab: emq >= 5 and < 8, limited to 10 rows
  const dashboardTableData = useMemo(() => {
    const filtered = pixelData.filter((pixel) => {
      const emqValue = safeNumber(pixel.emq);
      if (emqValue === null) return false;
      return emqValue >= 5 && emqValue < 8;
    });
    return filtered.slice(0, 10);
  }, [pixelData]);

  const breadcrumbLabelMap = {
    overview: 'Overview',
    pixels: 'Pixels',
    setupCapi: 'Setup CAPI',
    settings: 'Settings',
  };

  const breadcrumbLabel = breadcrumbLabelMap[activeTab] || 'Overview';

  const overviewStats = [
    {
      key: 'total-pixels',
      label: 'Total Pixels',
      value: metrics.totalActivePixels?.toLocaleString() || '0',
      changeType: 'positive',
      iconClass: 'fas fa-chart-line',
    },
    {
      key: 'high-quality',
      label: 'Healthy Pixels',
      value: metrics.healthyPixels?.toLocaleString() || '0',
      changeText: '≥8.0',
      changeType: 'positive',
      iconClass: 'fas fa-check-circle',
    },
    {
      key: 'issues-found',
      label: 'Needs Attention',
      value: metrics.needAttentionPixels?.toLocaleString() || '0',
      changeText: '5.0 - 7.9',
      changeType: 'neutral',
      iconClass: 'fas fa-exclamation-triangle',
    },
  ];

  const hasPixelData = displayPixelData.length > 0;
  const showDataFetchIndicator = syncing || (initialLoadComplete && loading);
  const isInitialDataPending =
    !initialLoadComplete ||
    loading ||
    (isAdmin && (!activeOrgId || adminOrgLoading));

  if (error) {
    return (
      <div className="error-message">
        <i className="fas fa-exclamation-circle" />
        <h3>Unable to load dashboard data</h3>
        <p>{error}</p>
        <button onClick={handleRetry} className="btn btn-primary">
            Retry
          </button>
      </div>
    );
  }

  return (
    <div className="dashboard-layout">
      <aside className="sidebar">
        <div className="logo">
          <img src={bigAtomLogo} alt="BIG ATOM BY ADYOGI" className="logo-image" />
        </div>
        <nav className="sidebar-nav">
          <ul className="nav-list">
            <li className="nav-item">
          <button 
                type="button"
                className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`}
                onClick={() => handleSidebarChange('overview')}
              >
                <span className="nav-link-icon">
                  <DashboardIcon className="nav-link-svg" aria-hidden="true" />
                </span>
                <span>Dashboard</span>
          </button>
            </li>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link ${activeTab === 'pixels' ? 'active' : ''}`}
                onClick={() => handleSidebarChange('pixels')}
              >
                <span className="nav-link-icon">
                  <PixelsIcon className="nav-link-svg" aria-hidden="true" />
                </span>
                <span>Pixels</span>
          </button>
            </li>
            <li className="nav-item">
          <button 
                type="button"
                className={`nav-link ${activeTab === 'setupCapi' ? 'active' : ''}`}
                onClick={() => handleSidebarChange('setupCapi')}
              >
                <span className="nav-link-icon">
                  <SetupCapiIcon className="nav-link-svg" aria-hidden="true" />
                </span>
                <span>Setup CAPI</span>
          </button>
            </li>
          </ul>
        </nav>
        <div className={`sidebar-user ${userMenuOpen ? 'open' : ''}`} ref={userMenuRef}>
          <button
            type="button"
            className="sidebar-user-toggle sidebar-user-toggle--avatar-only"
            onClick={(event) => {
              console.log('User avatar clicked', {
                email: displayEmail,
                role: displayRole,
                menuOpen: !userMenuOpen,
              });
              handleUserMenuToggle(event);
            }}
            aria-haspopup="true"
            aria-expanded={userMenuOpen}
          >
            <div className="sidebar-user-avatar" aria-hidden="true">
              {avatarInitial}
        </div>
          </button>
          {userMenuOpen && (
            <div className="sidebar-user-menu" role="menu">
              <div className="sidebar-user-menu-header">
                <div className="sidebar-user-menu-avatar">{avatarInitial}</div>
                <div className="sidebar-user-menu-details">
                  <div className="sidebar-user-menu-email">{displayEmail}</div>
                  <div className="sidebar-user-menu-role">{(displayRole || '').toUpperCase()}</div>
      </div>
              </div>
              <div className="sidebar-user-menu-divider" />
              <button
                type="button"
                className="sidebar-user-menu-item"
                onClick={() => {
                  setUserMenuOpen(false);
                  handleSettingsOpen();
                }}
                role="menuitem"
              >
                <span className="sidebar-user-menu-icon">
                  <i className="fas fa-gear" />
                </span>
                <span>Settings</span>
              </button>
              <button
                type="button"
                className="sidebar-user-menu-item sidebar-user-menu-item--logout"
                onClick={() => {
                  setUserMenuOpen(false);
                  handleLogout();
                }}
                role="menuitem"
              >
                <span className="sidebar-user-menu-icon">
                  <i className="fas fa-arrow-right-from-bracket" />
                </span>
                <span>Log Out</span>
              </button>
      </div>
          )}
      </div>
      </aside>

      <main className="main-content">
        <div className="content-sections">
          {isAdmin && (
            <div className="content-toolbar">
              <div className="content-toolbar-actions">
                <div className="content-org-selector">
                  <label htmlFor="admin-org-selector">Organization</label>
                  <div className="content-org-select">
                    <select
                      id="admin-org-selector"
                      value={activeOrgId ? String(activeOrgId) : ''}
                      onChange={handleOrgChange}
                      disabled={adminOrgLoading || !adminOrgOptions.length}
                    >
                      {adminOrgLoading ? (
                        <option value="">Loading organizations...</option>
                      ) : adminOrgOptions.length === 0 ? (
                        <option value="">No organizations</option>
                      ) : (
                        adminOrgOptions.map((org) => (
                          <option key={org.orgId} value={String(org.orgId)}>
                            {org.orgName}
                          </option>
                        ))
                      )}
                    </select>
                    {selectedOrgStatus && (
                      <span
                        className={`org-status-dot status-${(selectedOrgStatus.status || 'UNKNOWN')
                          .toLowerCase()
                          .replace(/\s+/g, '-')}`}
                        title={`Status: ${selectedOrgStatus.status || 'UNKNOWN'}`}
                        aria-label={`Status: ${selectedOrgStatus.status || 'UNKNOWN'}`}
                      />
                    )}
      </div>
                </div>
              </div>
            </div>
          )}
          {isAdmin && adminOrgError && (
            <div className="admin-org-banner error">
              <i className="fas fa-triangle-exclamation" />
              <span>{adminOrgError}</span>
      </div>
          )}
          {isAdmin && !adminOrgError && !adminOrgLoading && adminOrgOptions.length === 0 && (
            <div className="admin-org-banner">
              <i className="fas fa-circle-info" />
              <span>No organizations available to display yet.</span>
            </div>
          )}
          <section className={`content-section ${activeTab === 'overview' ? 'active' : ''}`} id="overview-section">
            <div className="overview-header">
              <h1>Dashboard</h1>
            </div>
            {isInitialDataPending ? (
              <div className="section-loading">
                <LoadingSpinner size="lg" message="Loading overview data..." />
              </div>
            ) : (
              <>
                {metaWarning && (
                  <div className="meta-warning-banner">
                    <i className="fas fa-circle-exclamation" />
                    <span>{metaWarning}</span>
                  </div>
                )}
                <div className="stats-grid">
                  {overviewStats.map((stat) => (
                    <div className="stat-card" key={stat.key}>
                      <div className="stat-icon">
                        <i className={stat.iconClass} />
                      </div>
                      <div className="stat-content">
                        <div className="stat-value">{stat.value}</div>
                        <div className="stat-label">{stat.label}</div>
                        <div className={`stat-change ${stat.changeType}`}>
                          
                          {stat.changeText}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {dashboardTableData.length > 0 && (
                  <div className="dashboard-table-section">
                    <h2 className="dashboard-table-title">Need Attention Pixels</h2>
                    <div className="pixels-table-wrapper">
                      <table>
                        <thead>
                          <tr>
                            <th>Pixel Name</th>
                            <th>Pixel ID</th>
                            <th>CAPI Status</th>
                            <th>Web EMQ</th>
                            <th>Offline EMQ</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dashboardTableData.map((pixel) => (
                            <tr key={pixel.id || pixel.dataset_id || pixel.name}>
                              <td className="pixel-name">{pixel.name || pixel.dataset_id || 'Unnamed Pixel'}</td>
                              <td className="pixel-id">{pixel.pixelId || pixel.dataset_id || pixel.id || '—'}</td>
                              <td>
                                <span className={`status-badge ${statusClass(pixel.capiStatus)}`}>
                                  {formatStatusLabel(pixel.capiStatus || 'Active')}
                                </span>
                              </td>
                              <td className={`pixel-score ${getScoreClass(pixel.emq)}`}>
                                {formatAverageScore(pixel.emq)}
                              </td>
                              <td className={`pixel-score ${getScoreClass(pixel.odq)}`}>
                                {formatAverageScore(pixel.odq)}
                              </td>
                              <td>
                                {String(pixel.capiStatus || '').toLowerCase().includes('disabl') ? (
                                  <button
                                    type="button"
                                    className="pixels-action-btn pixels-action-btn--secondary"
                                    onClick={handleEnableCapi}
                                  >
                                    Enable CAPI
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    className="pixels-action-btn pixels-action-btn--primary"
                                    onClick={() => handleRowClick(pixel)}
                                  >
                                    <i className="fas fa-eye" />
                                    View Details
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="dashboard-table-actions">
                      <button
                        type="button"
                        className="dashboard-view-all-btn"
                        onClick={() => handleSidebarChange('pixels')}
                      >
                        View All
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </section>

        <section className={`content-section ${activeTab === 'pixels' ? 'active' : ''}`} id="pixels-section">
          <div className="pixels-topbar">
            <div className="pixels-topbar-text">
              <h1>Pixel List</h1>
              <p>Manage your pixel integration and check real-time performance metrics.</p>
        </div>
            {formattedLastRefreshed && (
              <div className="pixels-last-updated">
                <i className="fas fa-clock" />
                <span>Last updated {formattedLastRefreshed}</span>
              </div>
            )}
          </div>
            {isInitialDataPending ? (
              <div className="section-loading">
                <LoadingSpinner size="md" message="Loading pixel data..." />
              </div>
            ) : (
              <>
                {showDataFetchIndicator && (
                  <div className="data-fetch-indicator" role="status" aria-live="polite">
                    <div className="data-fetch-progress">
                      <span className="data-fetch-progress-bar" />
                    </div>
                    <p>Fetching the latest pixels and metrics…</p>
                  </div>
                )}
                  <div className="pixels-card-toolbar">
                    <div className="pixels-search">
                      <i className="fas fa-search" />
                      <input
                        type="search"
                        placeholder="Search pixels by name or ID"
                        value={searchQuery}
                        onChange={(event) => handleSearchChange(event.target.value)}
         />
       </div>
                    <div className="pixels-toolbar-actions">
                      <button
                        type="button"
                        className="pixels-toolbar-btn pixels-toolbar-btn--refresh"
                        onClick={handleRefreshPixels}
                        disabled={syncing}
                      >
                        <i className="fas fa-rotate" />
                        <span>Refresh</span>
                        {syncing && <span className="refresh-spinner" aria-hidden="true" />}
                      </button>
       </div>


                  {metaWarning && (
                    <div className="meta-warning-banner">
                      <i className="fas fa-circle-exclamation" />
                      <span>{metaWarning}</span>
                    </div>
                  )}

                  <div className="pixels-table-wrapper">
                    {hasPixelData ? (
                      <table>
                        <thead>
                          <tr>
                            <th>Pixel Name</th>
                            <th>Pixel ID</th>
                            <th>CAPI Status</th>
                            <th>Web EMQ</th>
                            <th>Offline EMQ</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {displayPixelData.map((pixel) => (
                            <tr key={pixel.id || pixel.dataset_id || pixel.name}>
                              <td className="pixel-name">{pixel.name || pixel.dataset_id || 'Unnamed Pixel'}</td>
                              <td className="pixel-id">{pixel.pixelId || pixel.dataset_id || pixel.id || '—'}</td>
                              <td>
                                <span className={`status-badge ${statusClass(pixel.capiStatus)}`}>
                                  {formatStatusLabel(pixel.capiStatus || 'Active')}
                                </span>
                              </td>
                              <td className={`pixel-score ${getScoreClass(pixel.emq)}`}>
                                {formatAverageScore(pixel.emq)}
                              </td>
                              <td className={`pixel-score ${getScoreClass(pixel.odq)}`}>
                                {formatAverageScore(pixel.odq)}
                              </td>
                              <td>
                                {String(pixel.capiStatus || '').toLowerCase().includes('disabl') ? (
                                  <button
                                    type="button"
                                    className="pixels-action-btn pixels-action-btn--secondary"
                                    onClick={handleEnableCapi}
                                  >
                                    Enable CAPI
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    className="pixels-action-btn pixels-action-btn--primary"
                                    onClick={() => handleRowClick(pixel)}
                                  >
                                    <i className="fas fa-eye" />
                                    View Details
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="pixels-empty-state">
                        <div className="empty-icon">
                          <i className="fas fa-search" />
                        </div>
                        <h3>No Pixels Found</h3>
                        <p>No pixels match your search criteria or no pixels are available.</p>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

       <DrillDownModal
         isOpen={isModalOpen}
         onClose={handleModalClose}
         pixelData={selectedPixel}
        orgId={activeOrgId}
      />
        </section>

          <section
            className={`content-section setup-capi-section ${activeTab === 'setupCapi' ? 'active' : ''}`}
            id="setup-capi-section"
          >
            <div className="setup-capi-header">
              <h1>Set Up CAPI</h1>
              <p>Set Up CAPI for better data</p>
            </div>
            <div className="setup-capi-card">
              <div className="setup-capi-icon">
                <i className="fas fa-wrench" />
              </div>
              <div className="setup-capi-title">Supercharge Your Meta CAPI with BigAtom Signals</div>
              <div className="setup-capi-subtitle">
                Unlock higher match rates, richer customer insights, and more efficient ad delivery.
                <br /><br />
                BigAtom Signals sends enhanced, real-time events to Meta—ensuring your conversions are attributed accurately and your campaigns perform at their peak.
              </div>
              <button 
                type="button" 
                className="setup-capi-action" 
                onClick={handleGetStartedNow}
                disabled={emailSending}
              >
                {emailSending ? (
                  <>
                    <i className="fas fa-spinner fa-spin" />
                    <span>Sending Request...</span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-arrow-up-right-from-square" />
                    <span>Get Started Now</span>
                  </>
                )}
              </button>
              {emailSent && (
                <div className="setup-capi-message setup-capi-message--success">
                  <i className="fas fa-check-circle" />
                  <span>Request sent successfully! We'll get back to you soon.</span>
                </div>
              )}
              {emailError && (
                <div className="setup-capi-message setup-capi-message--error">
                  <i className="fas fa-exclamation-circle" />
                  <span>{emailError}</span>
                </div>
              )}
            </div>
          </section>

        <section className={`content-section ${activeTab === 'settings' ? 'active' : ''}`} id="settings-section">
          <SettingsInline orgId={activeOrgId} orgName={displayOrgName} initialSection={settingsSection} />
        </section>
      </div>
    </main>
     </div>
   );
 };

 export default Dashboard;